import { apiRequest } from '@/serves/http'
import ApiPath from '@/serves/path'
import { SpeechError, SpeechErrorCode } from './errors'

export const PLATFORM = 'web'

/** 开发期强制指定评测厂商用的 localStorage key */
const EVAL_VENDOR_KEY = 'hskmock:speech:evalVendor'

/**
 * 是否允许出现"手动指定评测厂商"这类调试能力。
 *
 * 只认非正式构建（VUE_APP_MODE 为 dev / beta）。`process.env.VUE_APP_MODE` 在打包时被替换成
 * 字面量，正式包里这个函数会被折叠成 `return false`，相关 UI 与传参路径整体不可达。
 */
export function isSpeechDebugEnabled() {
  return process.env.VUE_APP_MODE !== 'prod'
}

/** 凭证剩余不足这个时间就当过期，避免请求发出去时正好失效 */
const EXPIRY_GUARD_MS = 5000

export function getAppVersion() {
  return process.env.VUE_APP_VERSION || '0.0.0'
}

/**
 * 开发期用来强制后端下发某一家评测厂商（测试环境只有一套配置，默认下发 Azure，
 * 不这样做 SpeechSuper 那条支路没法验）。
 *
 * 正式环境**永远返回 undefined**：此时 assess() 回落到 SUPPORTED_EVAL_VENDORS，
 * 仍会上报端侧已打包的适配器列表，后端在该列表内按环境选择（国内 azure / 海外 speechsuper）。
 * 前端不参与"用哪一家"的决策。
 */
export function getEvalVendorOverride() {
  if (!isSpeechDebugEnabled()) return undefined
  let raw = ''
  try {
    raw = localStorage.getItem(EVAL_VENDOR_KEY) || ''
  } catch (e) {
    // 隐私模式下 localStorage 可能抛异常，忽略即可
  }
  if (!raw) return undefined
  const list = String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  return list.length ? list : undefined
}

/**
 * 设置/清除评测厂商覆盖。传空值表示恢复"由后端决定"。
 * 正式环境直接忽略——这条路径在正式包里不该被走到。
 *
 * 切换后会清掉 eval 凭证缓存，下一次录音立即用新厂商，不需要刷新页面。
 */
export function setEvalVendorOverride(vendor) {
  if (!isSpeechDebugEnabled()) return
  try {
    if (vendor) localStorage.setItem(EVAL_VENDOR_KEY, vendor)
    else localStorage.removeItem(EVAL_VENDOR_KEY)
  } catch (e) {
    console.warn('[speech] 无法写入 localStorage，厂商覆盖未生效', e)
    return
  }
  invalidateCapability('eval')
}

const cache = new Map()
const inflight = new Map()

const cacheKey = (capability, supportedVendors) =>
  `${capability}|${(supportedVendors || []).join(',')}`

function buildBody(capability, supportedVendors) {
  const body = {
    platform: PLATFORM,
    app_version: getAppVersion(),
    capability,
    // 必带：留空后端按 ws 下发接入点，浏览器的 WebSocket 设不了请求头，用不了
    protocol: 'http',
  }
  if (supportedVendors && supportedVendors.length) body.supported_vendors = supportedVendors
  return body
}

/**
 * 把网关返回收敛成 credential，并把签发上下文挂上去。
 *
 * apiRequest 成功时 resolve 的是整个响应信封 {errcode, errmsg, data}；
 * 验签不过时它 resolve 的是一个 Error，token 失效时 resolve undefined（已触发登出），
 * 这两种都当"没拿到凭证"处理。
 */
function unwrap(res, capability) {
  const data = res && !(res instanceof Error) && res.data
  const credential = data && data.credential
  if (!credential) {
    throw new SpeechError(SpeechErrorCode.CREDENTIAL_FAILED, 'no credential in response', {
      capability,
    })
  }
  credential.serviceStatus = data.service_status
  credential.bizCode = data.biz_code
  return credential
}

function failed(e, capability) {
  if (e instanceof SpeechError) return e
  // http.js 的响应拦截器在 errcode != 0 时 reject 的是 errmsg 字符串
  const message = typeof e === 'string' ? e : (e && e.message) || 'speech credential failed'
  return new SpeechError(SpeechErrorCode.CREDENTIAL_FAILED, message, { capability })
}

/**
 * 取凭证。按 ttl_seconds 内存缓存，同 key 并发只发一次请求。
 *
 * @param {'eval'} capability
 * @param {string[]} [supportedVendors] 端侧打包了哪几家适配器
 * @param {boolean} [forceRefresh] 跳过缓存
 * @returns {Promise<object>} credential
 * @throws {SpeechError} CREDENTIAL_FAILED
 */
export async function getCredential({ capability, supportedVendors, forceRefresh = false }) {
  const key = cacheKey(capability, supportedVendors)

  if (!forceRefresh) {
    const hit = cache.get(key)
    if (hit && hit.expiresAt - Date.now() > EXPIRY_GUARD_MS) return hit.credential
    const pending = inflight.get(key)
    if (pending) return pending
  }

  const task = (async () => {
    try {
      const res = await apiRequest.post(
        ApiPath.speech_credentials,
        buildBody(capability, supportedVendors)
      )
      const credential = unwrap(res, capability)
      cache.set(key, {
        credential,
        expiresAt: Date.now() + (credential.ttl_seconds || 0) * 1000,
      })
      return credential
    } catch (e) {
      throw failed(e, capability)
    } finally {
      inflight.delete(key)
    }
  })()

  inflight.set(key, task)
  return task
}

/**
 * 强制刷新凭证。**只在厂商拒绝凭证（401/403）时调一次**，不要循环调。
 * body 必须与签发时完全一致，否则命中不到同一枚凭证。
 */
export async function refreshCredential({ capability, supportedVendors }) {
  const key = cacheKey(capability, supportedVendors)
  try {
    const res = await apiRequest.post(
      ApiPath.speech_credential_refreshments,
      buildBody(capability, supportedVendors)
    )
    const credential = unwrap(res, capability)
    credential.refreshed = !!(res.data && res.data.refreshed)
    cache.set(key, {
      credential,
      expiresAt: Date.now() + (credential.ttl_seconds || 0) * 1000,
    })
    return credential
  } catch (e) {
    cache.delete(key)
    throw failed(e, capability)
  }
}

export function invalidateCredential({ capability, supportedVendors }) {
  cache.delete(cacheKey(capability, supportedVendors))
}

/** 清掉某个能力下的全部缓存（不关心是用哪份 supported_vendors 签发的） */
export function invalidateCapability(capability) {
  const prefix = capability + '|'
  for (const key of Array.from(cache.keys())) {
    if (key.indexOf(prefix) === 0) cache.delete(key)
  }
}

/**
 * 各厂商单次录音的时长上限。
 *
 * Azure 30 秒是官方文档写明的硬限制（短音频 REST 接口做发音评估不能超过 30 秒）；
 * SpeechSuper 60 秒是平台对接口径。2026-09-29 与产品确认：国内（Azure）30 秒、海外
 * （SpeechSuper）60 秒，水平测试的段落题由内容侧改短来适配。
 */
export function maxDurationMsFor(vendor) {
  return vendor === 'azure' ? 30000 : 60000
}
